import { openDB } from 'idb';

/**
 * @method getIDBObjects
 *
 * Opens a connection to IndexedDB and provides an instance of db, transaction, and store.
 *
 * @param {Number} bookPk - a Book primary key
 * @param {String} storeName - the name of the store to access
 * @param {Boolean} write - whether the connection should include write access (default: false)
 * @returns {Object} object containing a db instance, as well as a transaction and store if storeName was passed
 */
const getIDBObjects = async (bookPk, storeName, write = false) => {
  if (!bookPk) {
    throw new Error('A bookPk is required');
  }

  const databaseVersion = 1;
  const databaseName = `redshelf_${bookPk}`;
  const storeNames = [
    'graphql',
    'book',
    'highlights',
    'notes',
    'bookmarks',
    'copiedTexts',
    'flashcards',
    'flashcardDecks',
    'offlineSessions',
  ];

  if (!storeNames.includes(storeName)) {
    throw new Error('Invalid storeName specified.');
  }

  /**
   * Heads up! This is tricky.
   * Once a database is created on a user's machine,
   * we will need to bump the version number to make changes to it,
   * e.g. to add new types of user content items.
   */
  let db;
  try {
    db = await openDB(databaseName, databaseVersion, {
      upgrade(db, oldVersion) {
        if (oldVersion < databaseVersion) {
          for (let storeName of storeNames) {
            db.objectStoreNames.contains(storeName) ||
              db.createObjectStore(storeName);
          }
        }
      },
    });
  } catch (e) {
    console.warn(e);
    return;
  }

  // No storeName? Return only the db.
  if (!storeName) {
    return { db };
  }

  const transaction = db.transaction(
    storeName,
    write ? 'readwrite' : 'readonly',
  );

  const store = transaction.objectStore(storeName);

  return { db, transaction, store };
};

export default getIDBObjects;
